import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import NetworkMap from '../components/NetworkMap';
import axios from 'axios';
import { Box, DollarSign, TrendingUp, AlertCircle, Utensils } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Dashboard = ({ onRestartTour }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [noodlesStats, setNoodlesStats] = useState(null);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGlobalView, setShowGlobalView] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, noodlesRes, machinesRes] = await Promise.all([
        axios.get('/dashboard/stats'),
        axios.get('/dashboard/noodles-stats'),
        axios.get('/machines')
      ]);
      setStats(statsRes.data);
      setNoodlesStats(noodlesRes.data);
      setMachines(machinesRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center text-white">Chargement...</div>
      </Layout>
    );
  }

  return (
    <Layout onRestartTour={onRestartTour}>
      <div className="space-y-8 animate-fadeIn" data-testid="dashboard">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{fontFamily: 'Space Grotesk'}}>Bienvenue, {user?.name}</h1>
          <p className="text-gray-400">Vue d'ensemble de votre activité</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <Card className="glass-panel border-white/10 hover-lift animate-scaleIn" data-testid="stat-noodles-sold">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Nouilles Vendues</CardTitle>
              <Utensils className="text-[#d03367]" size={20} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" style={{fontFamily: 'Space Grotesk', color: '#d03367'}}>{stats?.noodles_sold_today || 0}</div>
              <p className="text-xs text-gray-500 mt-1">Aujourd'hui</p>
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/10 hover-lift animate-scaleIn" data-testid="stat-total-machines" style={{animationDelay: '0.1s'}}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Machines Totales</CardTitle>
              <Box className="text-[#d03367]" size={20} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" style={{fontFamily: 'Space Grotesk'}}>{stats?.total_machines || 0}</div>
              <p className="text-xs text-gray-500 mt-1">{stats?.active_machines || 0} actives</p>
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/10 hover-lift animate-scaleIn" data-testid="stat-today-revenue" style={{animationDelay: '0.2s'}}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">CA Aujourd'hui</CardTitle>
              <DollarSign className="text-[#d03367]" size={20} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" style={{fontFamily: 'Space Grotesk'}}>{stats?.today_revenue?.toFixed(2) || 0}€</div>
              <p className="text-xs text-green-400 mt-1">Marge: {stats?.today_margin?.toFixed(2) || 0}€</p>
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/10 hover-lift animate-scaleIn" data-testid="stat-fill-rate" style={{animationDelay: '0.3s'}}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Taux de Remplissage</CardTitle>
              <TrendingUp className="text-[#d03367]" size={20} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" style={{fontFamily: 'Space Grotesk'}}>{stats?.avg_fill_rate || 0}%</div>
              <p className="text-xs text-gray-500 mt-1">Moyenne globale</p>
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/10 hover-lift animate-scaleIn" data-testid="stat-refill-needed" style={{animationDelay: '0.4s'}}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Rechargements</CardTitle>
              <AlertCircle className="text-[#d03367]" size={20} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" style={{fontFamily: 'Space Grotesk'}}>{stats?.machines_needing_refill || 0}</div>
              <p className="text-xs text-orange-400 mt-1">À planifier</p>
            </CardContent>
          </Card>
        </div>

        {/* Noodles Statistics Widget */}
        {noodlesStats && (
          <Card className="glass-panel border-white/10 animate-slideIn" style={{background: 'linear-gradient(135deg, rgba(208, 51, 103, 0.1) 0%, rgba(0, 0, 0, 0.4) 100%)'}}>
            <CardHeader>
              <CardTitle className="text-2xl font-bold flex items-center gap-2" style={{fontFamily: 'Space Grotesk', color: '#d03367'}}>
                <Utensils size={28} />
                🍜 Statistiques de Ventes Nouilles
              </CardTitle>
              <p className="text-gray-400 text-sm">Performance des ventes sur différentes périodes</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {/* Since Creation */}
                <div className="text-center p-4 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-all">
                  <div className="text-xs text-gray-400 mb-2">Depuis la création</div>
                  <div className="text-3xl font-bold text-white mb-1" style={{fontFamily: 'Space Grotesk'}}>
                    {noodlesStats.since_creation?.toLocaleString() || 0}
                  </div>
                  <div className="text-xs text-gray-500">Total historique</div>
                </div>

                {/* This Month */}
                <div className="text-center p-4 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-all">
                  <div className="text-xs text-gray-400 mb-2">Ce mois</div>
                  <div className="text-3xl font-bold text-blue-400 mb-1" style={{fontFamily: 'Space Grotesk'}}>
                    {noodlesStats.this_month?.toLocaleString() || 0}
                  </div>
                  <div className="text-xs text-gray-500">
                    {noodlesStats.since_creation > 0 
                      ? `${((noodlesStats.this_month / noodlesStats.since_creation) * 100).toFixed(1)}% du total`
                      : '0%'}
                  </div>
                </div>

                {/* This Week */}
                <div className="text-center p-4 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-all">
                  <div className="text-xs text-gray-400 mb-2">Cette semaine</div>
                  <div className="text-3xl font-bold text-purple-400 mb-1" style={{fontFamily: 'Space Grotesk'}}>
                    {noodlesStats.this_week?.toLocaleString() || 0}
                  </div>
                  <div className="text-xs text-gray-500">7 derniers jours</div>
                </div>

                {/* Today */}
                <div className="text-center p-4 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-all">
                  <div className="text-xs text-gray-400 mb-2">Aujourd'hui</div>
                  <div className="text-3xl font-bold text-green-400 mb-1" style={{fontFamily: 'Space Grotesk'}}>
                    {noodlesStats.today?.toLocaleString() || 0}
                  </div>
                  <div className="text-xs text-gray-500">Ventes du jour</div>
                </div>

                {/* This Hour */}
                <div className="text-center p-4 rounded-lg bg-white/5 border border-[#d03367]/30 hover:bg-white/10 transition-all">
                  <div className="text-xs text-gray-400 mb-2">Cette heure</div>
                  <div className="text-3xl font-bold mb-1" style={{fontFamily: 'Space Grotesk', color: '#d03367'}}>
                    {noodlesStats.this_hour?.toLocaleString() || 0}
                  </div>
                  <div className="text-xs text-gray-500">Moyenne horaire</div>
                </div>
              </div>

              {/* Performance Indicator */}
              <div className="mt-4 p-3 rounded-lg bg-gradient-to-r from-[#d03367]/20 to-transparent border-l-4 border-[#d03367]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp size={20} className="text-[#d03367]" />
                    <span className="text-white font-medium">Performance globale</span>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-white">
                      {noodlesStats.today > 0 && noodlesStats.this_month > 0
                        ? `${((noodlesStats.today / (noodlesStats.this_month / new Date().getDate())) * 100).toFixed(0)}%`
                        : '0%'}
                    </div>
                    <div className="text-xs text-gray-400">vs moyenne mensuelle</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Network Map */}
        <Card className="glass-panel border-white/10 animate-slideIn">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="text-xl font-bold text-white mb-1" style={{fontFamily: 'Space Grotesk'}}>
                  {showGlobalView ? '🌍 Carte France Entière' : '🗺️ Votre Écosystème'}
                </CardTitle>
                <p className="text-gray-400 text-sm">
                  {showGlobalView 
                    ? 'Tous les licenciés, gestionnaires et machines Noodly en France'
                    : user?.role === 'groupe' 
                      ? 'Vue d\'ensemble de tout le réseau Noodly'
                      : user?.role === 'licencie'
                        ? 'Vos gestionnaires et toutes leurs machines'
                        : user?.role === 'gestionnaire'
                          ? 'Vos machines et vos opérateurs'
                          : 'Les machines de votre gestionnaire'
                  }
                </p>
              </div>
              <Button
                onClick={() => setShowGlobalView(!showGlobalView)}
                variant="outline"
                className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
              >
                {showGlobalView ? '👤 Mon Écosystème' : '🌍 Vue France'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div style={{ height: '500px', borderRadius: '8px', overflow: 'hidden' }}>
              <NetworkMap globalView={showGlobalView} />
            </div>
          </CardContent>
        </Card>

        {/* Recent Machines */}
        <Card className="glass-panel border-white/10 animate-slideIn" style={{animationDelay: '0.1s'}}>
          <CardHeader>
            <CardTitle className="text-xl font-bold" style={{fontFamily: 'Space Grotesk'}}>Machines Récentes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="recent-machines-list">
              {machines.slice(0, 5).map((machine, index) => (
                <div 
                  key={machine.id} 
                  onClick={() => navigate(`/machines/${machine.id}`)}
                  className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-all hover:transform hover:scale-[1.02] cursor-pointer" 
                  style={{animationDelay: `${index * 0.05}s`}}
                >
                  <div>
                    <p className="font-medium text-white hover:text-[#d03367] transition-colors">{machine.company_name}</p>
                    <p className="text-sm text-gray-400">{machine.city}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2">
                      <div className="text-sm">
                        <span className="font-medium">{machine.current_stock}</span>
                        <span className="text-gray-400">/{machine.capacity}</span>
                      </div>
                      <div className={`w-2 h-2 rounded-full animate-pulse ${
                        machine.current_stock / machine.capacity > 0.5 ? 'bg-green-400' :
                        machine.current_stock / machine.capacity > 0.3 ? 'bg-orange-400' : 'bg-red-400'
                      }`} />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 capitalize">{machine.status}</p>
                  </div>
                </div>
              ))}
              {machines.length === 0 && (
                <p className="text-center text-gray-400 py-8">Aucune machine pour le moment</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Dashboard;
